import { createPortal } from 'react-dom'
import Icon from 'src/components/icons/icon'
import { useGuidedTour } from './use-guided-tour'
import { TourStep } from './tour-steps'

const PADDING = 8
const TOOLTIP_WIDTH = 320
const TOOLTIP_GAP = 12

const computeTooltipStyle = (rect: DOMRect, placement: TourStep['placement']): React.CSSProperties => {
  switch (placement) {
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + PADDING + TOOLTIP_GAP,
        transform: 'translateY(-50%)',
      }
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - PADDING - TOOLTIP_GAP - TOOLTIP_WIDTH,
        transform: 'translateY(-50%)',
      }
    case 'bottom':
      return {
        top: rect.bottom + PADDING + TOOLTIP_GAP,
        left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
      }
    case 'top':
      return {
        top: rect.top - PADDING - TOOLTIP_GAP,
        left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
        transform: 'translateY(-100%)',
      }
  }
}

export const GuidedTour = () => {
  const { isActive, currentStep, targetRect, stepConfig, totalSteps, prevStep, nextStep, dismissTour } = useGuidedTour()

  if (!isActive || !targetRect || !stepConfig) return null

  const isLastStep = currentStep === totalSteps - 1

  return createPortal(
    <>
      {/* Backdrop - click to dismiss */}
      <div className='fixed inset-0 z-[200]' onClick={dismissTour} />

      {/* Spotlight cutout */}
      <div
        className='pointer-events-none fixed z-[201] rounded-lg transition-all duration-300'
        style={{
          top: targetRect.top - PADDING,
          left: targetRect.left - PADDING,
          width: targetRect.width + PADDING * 2,
          height: targetRect.height + PADDING * 2,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
        }}
      />

      {/* Tooltip */}
      <div
        className='fixed z-[202] rounded-xl bg-white p-5 shadow-2xl'
        style={{
          width: TOOLTIP_WIDTH,
          ...computeTooltipStyle(targetRect, stepConfig.placement),
        }}
        role='dialog'
        aria-modal='true'
      >
        {/* Close button */}
        <button
          onClick={dismissTour}
          className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-gray-600'
          aria-label='Close tour'
        >
          <Icon name='x' size={16} />
        </button>

        {/* Content */}
        <p className='text-brand text-xs font-bold'>
          Step {currentStep + 1}: {stepConfig.title}
        </p>
        <p className='mt-2 text-sm leading-relaxed text-gray-500'>{stepConfig.description}</p>

        {/* Footer */}
        <div className='mt-4 flex items-center justify-between'>
          <span className='text-xs text-gray-400'>
            {currentStep + 1} of {totalSteps}
          </span>
          <div className='flex items-center gap-2'>
            {currentStep > 0 && (
              <button
                onClick={prevStep}
                className='flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium text-gray-400 transition-colors hover:text-gray-600'
              >
                <Icon name='lucide-arrow-left' size={14} />
                Back
              </button>
            )}
            <button
              onClick={isLastStep ? dismissTour : nextStep}
              className='bg-brand hover:bg-brand/90 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white'
            >
              {isLastStep ? 'Done' : 'Next'}
              {!isLastStep && <Icon name='lucide-arrow-right' size={14} />}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
